import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  KeyRound, Mail, ShieldCheck, ShieldOff, ShieldAlert, Eye, EyeOff, Copy,
  Truck, Zap,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const ROLE_OPTIONS = [
  { value: "employee", label: "Employee (Regular staff)" },
  { value: "manager", label: "Manager (Team lead)" },
  { value: "hod", label: "HOD (Department Head)" },
  { value: "senior_management", label: "Senior Management (Owners/Directors)" },
  { value: "ea", label: "EA (Executive Assistant)" },
];

// Which hierarchy dropdowns show for each role
const SHOWS = {
  employee:           { manager: true,  hod: true,  senior: true,  ea_for: false },
  manager:            { manager: false, hod: true,  senior: true,  ea_for: false },
  hod:                { manager: false, hod: false, senior: true,  ea_for: false },
  senior_management:  { manager: false, hod: false, senior: false, ea_for: false },
  ea:                 { manager: false, hod: false, senior: false, ea_for: true },
};

const fmtDateTime = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return s;
  }
};

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return s;
  }
};

/**
 * Portal Access section — embedded as the 6th tab of the employee edit page.
 *
 * Handles the entire credential lifecycle:
 *   Enable (first time)  → POST /employees/{id}/portal/enable
 *   Reset password       → POST /employees/{id}/portal/reset-password
 *   Update role/manager  → PATCH /employees/{id}/portal
 *   Disable / Re-enable  → POST /employees/{id}/portal/disable | /enable-existing
 *
 * When a new temp password is issued the backend returns it ONCE — we display
 * it inline in a "show / copy" panel so the admin can communicate it to the
 * employee. After dismissing the panel, the password cannot be retrieved.
 */
export default function PortalAccessSection({ employee, canManage, onChanged }) {
  const [managerCandidates, setManagerCandidates] = useState([]);
  const [hodCandidates, setHodCandidates] = useState([]);
  const [seniorCandidates, setSeniorCandidates] = useState([]);
  const [email, setEmail] = useState(employee?.email || "");
  const [role, setRole] = useState(employee?.role || "employee");
  const [reportsToManager, setReportsToManager] = useState(employee?.reports_to_manager || "");
  const [reportsTo, setReportsTo] = useState(
    employee?.reports_to_hod || employee?.reports_to || ""
  );
  const [seniorIds, setSeniorIds] = useState(() => {
    const list = employee?.reports_to_senior_mgmt_ids;
    if (Array.isArray(list) && list.length) return list.filter(Boolean);
    return employee?.reports_to_senior_mgmt ? [employee.reports_to_senior_mgmt] : [];
  });
  const [eaFor, setEaFor] = useState(() => {
    const list = employee?.ea_for;
    return Array.isArray(list) ? list.filter(Boolean) : [];
  });
  const [busy, setBusy] = useState(false);
  const [pwdReveal, setPwdReveal] = useState(null); // { password, email }
  const [showPwd, setShowPwd] = useState(false);

  // Reset local state when the employee prop changes (e.g. after refetch)
  useEffect(() => {
    setEmail(employee?.email || "");
    setRole(employee?.role || "employee");
    setReportsToManager(employee?.reports_to_manager || "");
    setReportsTo(employee?.reports_to_hod || employee?.reports_to || "");
    const list = employee?.reports_to_senior_mgmt_ids;
    if (Array.isArray(list) && list.length) setSeniorIds(list.filter(Boolean));
    else setSeniorIds(employee?.reports_to_senior_mgmt ? [employee.reports_to_senior_mgmt] : []);
    setEaFor(Array.isArray(employee?.ea_for) ? employee.ea_for.filter(Boolean) : []);
  }, [employee?.id, employee?.email, employee?.role,
      employee?.reports_to, employee?.reports_to_hod,
      employee?.reports_to_manager,
      employee?.reports_to_senior_mgmt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load candidate lists once (per employee dept context)
  useEffect(() => {
    (async () => {
      try {
        const [mgr, hod, sen] = await Promise.all([
          api.get("/portal/hod-candidates", { params: { role_filter: "manager", department_id: employee?.department_id } }),
          api.get("/portal/hod-candidates", { params: { role_filter: "hod", department_id: employee?.department_id } }),
          api.get("/portal/hod-candidates", { params: { role_filter: "senior" } }),
        ]);
        setManagerCandidates(mgr.data || []);
        setHodCandidates(hod.data || []);
        setSeniorCandidates(sen.data || []);
      } catch {
        /* non-fatal */
      }
    })();
  }, [employee?.department_id]);

  const hasPortal = Boolean(employee?.email);
  const isActive = employee?.is_active !== false; // employees default true

  // Helper — extracts email_in_use structured error and lets HR
  // confirm force-reassignment. Returns true if user confirmed, false otherwise.
  const confirmForceReassign = (detail) => {
    const name = detail?.conflict_employee_name || "Unknown";
    const punch = detail?.conflict_employee_punch_id
      ? ` (Punch: ${detail.conflict_employee_punch_id})` : "";
    return window.confirm(
      `This email is currently used by ${name}${punch}.\n\n` +
      `They are still marked ACTIVE. If you continue, their portal ` +
      `access will be disabled, their email released, and this employee ` +
      `will take over the email.\n\n` +
      `Continue with force-reassign? / Kya aap force-reassign karna chahte hain?`
    );
  };

  const enablePortal = async () => {
    if (!email) {
      toast.error("Email required / Email zaroori hai");
      return;
    }
    setBusy(true);
    const body = {
      email: email.trim().toLowerCase(),
      role,
      reports_to_manager: reportsToManager || null,
      reports_to_hod: reportsTo || null,
      reports_to_senior_mgmt_ids: seniorIds,
      ea_for: role === "ea" ? eaFor : [],
    };
    try {
      const { data } = await api.post(`/employees/${employee.id}/portal/enable`, body);
      setPwdReveal({ password: data.temporary_password, email: data.email });
      setShowPwd(true);
      toast.success("Portal access enabled. Note the temp password below.");
      await onChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Handle structured 409 email_in_use — offer force-reassign
      if (e.response?.status === 409 && detail?.code === "email_in_use") {
        if (confirmForceReassign(detail)) {
          try {
            const { data } = await api.post(
              `/employees/${employee.id}/portal/enable`,
              { ...body, force_reassign: true },
            );
            setPwdReveal({ password: data.temporary_password, email: data.email });
            setShowPwd(true);
            toast.success(
              `Email released from ${detail.conflict_employee_name} and portal enabled here.`
            );
            await onChanged?.();
          } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail));
          }
        }
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setBusy(false);
    }
  };

  const resetPwd = async () => {
    if (!window.confirm("Reset password for this employee? They will be forced to change it on next login."))
      return;
    setBusy(true);
    try {
      const { data } = await api.post(`/employees/${employee.id}/portal/reset-password`);
      setPwdReveal({ password: data.temporary_password, email: data.email });
      setShowPwd(true);
      toast.success("Password reset. Share the new temp password below.");
      await onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const disablePortal = async () => {
    if (!window.confirm("Disable portal access? The employee will not be able to log in."))
      return;
    setBusy(true);
    try {
      await api.post(`/employees/${employee.id}/portal/disable`);
      toast.success("Portal access disabled.");
      await onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reactivatePortal = async () => {
    setBusy(true);
    try {
      await api.post(`/employees/${employee.id}/portal/enable-existing`);
      toast.success("Portal access re-activated.");
      await onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const updateRoleManager = async () => {
    setBusy(true);
    const trimmedEmail = (email || "").trim().toLowerCase();
    const emailChanged = hasPortal && trimmedEmail && trimmedEmail !== (employee?.email || "").toLowerCase();
    const body = {
      ...(emailChanged ? { email: trimmedEmail } : {}),
      role,
      reports_to_manager: reportsToManager || null,
      reports_to_hod: reportsTo || null,
      reports_to_senior_mgmt_ids: seniorIds,
      ea_for: role === "ea" ? eaFor : [],
    };
    try {
      await api.patch(`/employees/${employee.id}/portal`, body);
      toast.success(emailChanged ? "Updated · portal email changed" : "Updated.");
      await onChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "email_in_use") {
        if (confirmForceReassign(detail)) {
          try {
            await api.patch(`/employees/${employee.id}/portal`,
              { ...body, force_reassign: true });
            toast.success(
              `Email released from ${detail.conflict_employee_name} and reassigned here.`
            );
            await onChanged?.();
          } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail));
          }
        }
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setBusy(false);
    }
  };

  const copyPwd = async () => {
    try {
      await navigator.clipboard.writeText(pwdReveal.password);
      toast.success("Password copied to clipboard");
    } catch {
      toast.error("Copy failed — please select and copy manually");
    }
  };

  const sendWelcomeStub = () => {
    toast("Welcome email is delivered automatically in Phase 1D (email integration).", {
      description: "For now, share the temp password manually with the employee.",
    });
  };

  // ---------- Render ----------
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Portal Access
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Manage this employee's HR portal login credentials.
            <br />
            <span className="text-slate-400">
              Is employee ke HR portal login credentials manage karein.
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          {!hasPortal && (
            <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-300">
              <ShieldOff className="h-3 w-3 mr-1" /> Not enabled
            </Badge>
          )}
          {hasPortal && isActive && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">
              <ShieldCheck className="h-3 w-3 mr-1" /> Active
            </Badge>
          )}
          {hasPortal && !isActive && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
              <ShieldAlert className="h-3 w-3 mr-1" /> Disabled
            </Badge>
          )}
        </div>
      </div>

      {/* Temp password reveal panel */}
      {pwdReveal && (
        <div
          data-testid="portal-temp-pwd-panel"
          className="border-2 border-amber-300 bg-amber-50 rounded-md p-4"
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <div className="font-bold text-amber-900 text-sm">
                ⚠ Save this password — it will not be shown again
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                Yeh password ek baar copy kar lijiye — ab dobara dikhayi nahi dega.
              </div>
            </div>
            <button
              onClick={() => setPwdReveal(null)}
              data-testid="portal-pwd-dismiss"
              className="text-xs text-amber-800 hover:text-amber-900 underline"
            >
              Done
            </button>
          </div>
          <div className="flex items-center gap-2 bg-white border border-amber-200 rounded px-3 py-2">
            <div className="text-xs text-slate-500 uppercase tracking-wider mr-2">Pwd</div>
            <code
              data-testid="portal-pwd-value"
              className="flex-1 font-mono text-base text-slate-900"
            >
              {showPwd ? pwdReveal.password : "•".repeat(pwdReveal.password.length)}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="portal-pwd-toggle"
              onClick={() => setShowPwd(!showPwd)}
            >
              {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="portal-pwd-copy"
              onClick={copyPwd}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-[11px] text-amber-800 mt-2">
            Email: <code>{pwdReveal.email}</code>
          </div>
        </div>
      )}

      {/* Credentials form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Driver Quick-Setup banner — only for category=driver employees who don't have portal yet */}
        {!hasPortal && String(employee?.category || "").toLowerCase() === "driver" && (
          <div className="md:col-span-2 rounded-xl ring-1 ring-amber-300 bg-gradient-to-r from-amber-50 to-amber-100 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3"
               data-testid="portal-driver-quick-banner">
            <div className="h-12 w-12 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
              <Truck className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-amber-900">
                Driver detected · Quick login setup
              </div>
              <div className="text-[11px] text-amber-800 mt-0.5">
                1 click se Driver mobile app ka login ban jayega.
                Email format: <code className="bg-white/60 px-1 rounded">driver.{employee?.punch_id || "{punch_id}"}@bravurapackers.com</code>
                {!employee?.punch_id && (
                  <span className="block mt-1 text-red-700 font-semibold">
                    ⚠ Punch ID missing in Personal tab — set it first to use quick setup.
                  </span>
                )}
              </div>
            </div>
            <Button
              type="button"
              disabled={!canManage || busy || !employee?.punch_id}
              data-testid="portal-driver-quick-btn"
              onClick={async () => {
                const suggested = `driver.${String(employee.punch_id).toLowerCase()}@bravurapackers.com`;
                setEmail(suggested);
                setRole("employee");
                // small delay so React state lands before the API call uses
                // the current values from the form closure; call enable
                // directly with explicit overrides to avoid race.
                setBusy(true);
                try {
                  const { data } = await api.post(
                    `/employees/${employee.id}/portal/enable`,
                    {
                      email: suggested,
                      role: "employee",
                      reports_to_manager: reportsToManager || null,
                      reports_to_hod: reportsTo || null,
                      reports_to_senior_mgmt_ids: seniorIds,
                      ea_for: [],
                    },
                  );
                  setPwdReveal({ password: data.temporary_password, email: data.email });
                  setShowPwd(true);
                  toast.success("Driver login ready — copy the password to share via WhatsApp.");
                  await onChanged?.();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail));
                } finally {
                  setBusy(false);
                }
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0 gap-1"
            >
              <Zap className="h-4 w-4" /> Generate Driver Login
            </Button>
          </div>
        )}

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            Email (Login ID)
          </Label>
          <Input
            data-testid="portal-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={!canManage}
            placeholder="firstname.lastname@bravurapackers.com"
            className="mt-1.5"
          />
          <p className="text-[11px] text-slate-400 mt-1">
            {hasPortal
              ? "Change email anytime — click \"Update Role & Hierarchy\" below to save."
              : "Used as the portal login ID. Must be unique across all accounts."}
          </p>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            Role
          </Label>
          <Select value={role} onValueChange={setRole} disabled={!canManage}>
            <SelectTrigger data-testid="portal-role-select" className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {String(employee?.category || "").toLowerCase() === "driver" && (
            <p className="text-[11px] text-amber-700 mt-1">
              <strong>Driver:</strong> Keep role as <b>Employee</b> — system auto-routes
              this user to the Driver mobile app based on <b>Category</b> field.
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Reporting Hierarchy
            </div>
            <span className="text-[10px] text-slate-400">
              · Auto-hides per role
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 border border-slate-200 rounded-md p-4">
            {SHOWS[role]?.manager && (
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                  Manager (Direct Approver)
                </Label>
                <Select
                  value={reportsToManager || "__none__"}
                  onValueChange={(v) => setReportsToManager(v === "__none__" ? "" : v)}
                  disabled={!canManage}
                >
                  <SelectTrigger data-testid="portal-manager-select" className="mt-1.5">
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {managerCandidates
                      .filter((c) => c.id !== employee?.id)
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} {c.punch_id ? `(${c.punch_id})` : ""}
                          {c.designation ? ` — ${c.designation}` : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {managerCandidates.filter((c) => c.id !== employee?.id).length === 0 ? (
                  <p className="text-[10px] text-amber-700 mt-1">
                    ⚠ No Managers yet — set someone's role to <b>Manager</b> in their Employment tab first.
                  </p>
                ) : (
                  <p className="text-[10px] text-slate-400 mt-1">Receives Approve/Reject email.</p>
                )}
              </div>
            )}

            {SHOWS[role]?.hod && (
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                  HOD (Department Head)
                </Label>
                <Select
                  value={reportsTo || "__none__"}
                  onValueChange={(v) => setReportsTo(v === "__none__" ? "" : v)}
                  disabled={!canManage}
                >
                  <SelectTrigger data-testid="portal-hod-select" className="mt-1.5">
                    <SelectValue placeholder="Select HOD" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {hodCandidates
                      .filter((c) => c.id !== employee?.id)
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} {c.punch_id ? `(${c.punch_id})` : ""}
                          {c.designation ? ` — ${c.designation}` : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {hodCandidates.filter((c) => c.id !== employee?.id).length === 0 ? (
                  <p className="text-[10px] text-amber-700 mt-1">
                    ⚠ No HODs yet — set someone's role to <b>HOD</b> in their Employment tab first.
                  </p>
                ) : (
                  <p className="text-[10px] text-slate-400 mt-1">CC'd for visibility + escalation.</p>
                )}
              </div>
            )}

            {SHOWS[role]?.senior && (
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                  Senior Management (Multi)
                </Label>
                <div
                  className="mt-1.5 border border-slate-200 rounded-md bg-white p-2 space-y-1 max-h-44 overflow-auto"
                  data-testid="portal-senior-multi"
                >
                  {seniorCandidates.length === 0 && (
                    <p className="text-xs text-slate-400 px-1 py-2">No Senior Mgmt yet.</p>
                  )}
                  {seniorCandidates
                    .filter((c) => c.id !== employee?.id)
                    .map((c) => {
                      const checked = seniorIds.includes(c.id);
                      return (
                        <label
                          key={c.id}
                          data-testid={`portal-senior-${c.id}`}
                          className={`flex items-start gap-2 px-2 py-1 rounded cursor-pointer text-sm ${
                            checked ? "bg-red-50" : "hover:bg-slate-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            disabled={!canManage}
                            checked={checked}
                            onChange={(e) => {
                              setSeniorIds((cur) =>
                                e.target.checked
                                  ? [...cur, c.id]
                                  : cur.filter((x) => x !== c.id)
                              );
                            }}
                            className="mt-0.5"
                          />
                          <div className="leading-tight">
                            <div className="font-medium">{c.name}</div>
                            <div className="text-[10px] text-slate-400">
                              {c.designation || (c.punch_id ? `Punch ${c.punch_id}` : "")}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">CC'd on every leave for visibility. Override power.</p>
              </div>
            )}

            {SHOWS[role]?.ea_for && (
              <div className="md:col-span-3">
                <Label className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  EA — Acts On Behalf Of (Senior Mgmt)
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-1.5 max-h-44 overflow-y-auto border border-red-100 rounded p-2 bg-red-50/30"
                     data-testid="ea-for-multi">
                  {seniorCandidates.length === 0 ? (
                    <div className="col-span-full text-xs text-slate-500 italic">
                      No Senior Mgmt employees available yet.
                    </div>
                  ) : seniorCandidates.map((c) => {
                    const checked = eaFor.includes(c.id);
                    return (
                      <label key={c.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer border ${
                          checked ? "bg-white border-red-200" : "bg-white/60 border-transparent hover:border-slate-200"
                        }`}
                        data-testid={`ea-for-checkbox-${c.id}`}>
                        <input type="checkbox" checked={checked}
                          onChange={(e) => {
                            setEaFor((prev) => e.target.checked
                              ? [...prev, c.id]
                              : prev.filter((x) => x !== c.id));
                          }}
                          className="h-3.5 w-3.5 accent-red-600"
                          disabled={!canManage} />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-900 truncate">{c.name}</div>
                          <div className="text-[10px] text-slate-400">
                            {c.designation || (c.punch_id ? `Punch ${c.punch_id}` : "")}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  EA can create &amp; view tasks only on behalf of these Senior Mgmt principals.
                </p>
              </div>
            )}

            {!SHOWS[role]?.manager && !SHOWS[role]?.hod && !SHOWS[role]?.senior && !SHOWS[role]?.ea_for && (
              <div className="md:col-span-3 text-sm text-slate-500 italic">
                {role === "senior_management"
                  ? "No reporting above — top of hierarchy."
                  : "—"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
        {!hasPortal && (
          <Button
            data-testid="portal-enable-btn"
            onClick={enablePortal}
            disabled={busy || !canManage || !email}
            className="brand-bg brand-bg-hover text-white"
          >
            <ShieldCheck className="h-4 w-4 mr-2" />
            Enable Portal Access
          </Button>
        )}
        {hasPortal && (
          <>
            <Button
              type="button"
              variant="outline"
              data-testid="portal-update-btn"
              onClick={updateRoleManager}
              disabled={busy || !canManage}
            >
              Update role / manager
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="portal-reset-pwd-btn"
              onClick={resetPwd}
              disabled={busy || !canManage}
              className="text-amber-700 hover:bg-amber-50"
            >
              <KeyRound className="h-4 w-4 mr-2" />
              Reset Password
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="portal-send-welcome-btn"
              onClick={sendWelcomeStub}
              disabled={busy}
            >
              <Mail className="h-4 w-4 mr-2" />
              Send Welcome Email
            </Button>
            {isActive && (
              <Button
                type="button"
                variant="outline"
                data-testid="portal-disable-btn"
                onClick={disablePortal}
                disabled={busy || !canManage}
                className="text-red-700 hover:bg-red-50 ml-auto"
              >
                <ShieldOff className="h-4 w-4 mr-2" />
                Disable Account
              </Button>
            )}
            {!isActive && (
              <Button
                type="button"
                variant="outline"
                data-testid="portal-reactivate-btn"
                onClick={reactivatePortal}
                disabled={busy || !canManage}
                className="text-emerald-700 hover:bg-emerald-50 ml-auto"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Enable Portal Access
              </Button>
            )}
          </>
        )}
      </div>

      {/* Metadata */}
      {hasPortal && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs pt-3 border-t border-slate-100">
          <div>
            <div className="uppercase tracking-wider text-slate-400 mb-0.5">First Login Required</div>
            <div className="text-slate-700 font-medium">
              {employee?.first_login ? "Yes — pending" : "No"}
            </div>
          </div>
          <div>
            <div className="uppercase tracking-wider text-slate-400 mb-0.5">Last Login</div>
            <div className="text-slate-700 font-medium">{fmtDateTime(employee?.last_login)}</div>
          </div>
          <div>
            <div className="uppercase tracking-wider text-slate-400 mb-0.5">Account Created</div>
            <div className="text-slate-700 font-medium">{fmtDate(employee?.account_created_at)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
